import React from 'react';
import { Box, Flex, Text } from 'rebass/styled-components';
import { Button, Loader, Message, Modal, Tooltip } from '@getro/rombo';
import MfaStatusBadge from 'components/atoms/mfaStatusBadge';

import BackupCodes from './BackupCodes';
import MfaCodeModal from './MfaCodeModal';
import MfaSetupModal from './MfaSetupModal';
import { useMfaSettings, MFA_VIEW } from './useMfaSettings';

const DISABLE_2FA_NETWORK_REQUIRED_TOOLTIP =
  "You can't disable two-factor authentication because one or more networks you belong to require having it enabled.";

const MFASettings = () => {
  const {
    enabled,
    requiredByNetwork,
    isStatusLoading,
    isStatusError,
    isSettingUp,
    view,
    setView,
    setupData,
    backupCodes,
    actionError,
    startSetup,
    confirmEnable,
    confirmDisable,
    startRegenerate,
    confirmRegenerate,
    finishBackupCodes,
    cancel,
  } = useMfaSettings();

  const showContent = !isStatusLoading && !isStatusError;

  return (
    <Flex sx={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
      <Flex alignItems="center" sx={{ gap: '8px' }}>
        <Text fontSize="14px" fontWeight="medium" color="text.dark" sx={{ lineHeight: 1.4 }}>
          Two-factor authentication
        </Text>
        {showContent && <MfaStatusBadge enabled={enabled} />}
      </Flex>
      <Text fontSize="14px">
        Add an extra layer of security: when you log in, you will be asked for a second authentication code generated by
        an authentication app such as Google Authenticator or Authy.
      </Text>
      {isStatusLoading && (
        <Box py={2}>
          <Loader size="small" />
        </Box>
      )}
      {isStatusError && <Message type="error" content="Could not load two-factor authentication status." />}
      {showContent && (
        <>
          {enabled ? (
            <Flex alignItems="center" justifyContent="space-between" sx={{ gap: 3, flexWrap: 'wrap' }}>
              <Flex sx={{ gap: 2, flexShrink: 0 }}>
                <Button size="small" variant="secondary" onClick={startRegenerate} data-cy="mfa-regenerate">
                  Regenerate backup codes
                </Button>
                {requiredByNetwork ? (
                  <Tooltip
                    position="top"
                    content={<Text fontSize="14px">{DISABLE_2FA_NETWORK_REQUIRED_TOOLTIP}</Text>}
                    trigger={
                      <Box as="span" sx={{ display: 'inline-flex' }}>
                        <Button size="small" variant="secondary" disabled data-cy="mfa-disable">
                          Disable 2FA
                        </Button>
                      </Box>
                    }
                  />
                ) : (
                  <Button
                    size="small"
                    variant="secondary"
                    onClick={() => setView(MFA_VIEW.DISABLE)}
                    data-cy="mfa-disable"
                  >
                    Disable 2FA
                  </Button>
                )}
              </Flex>
            </Flex>
          ) : (
            <Button size="small" variant="secondary" loading={isSettingUp} onClick={startSetup} data-cy="mfa-enable">
              Enable two-factor authentication
            </Button>
          )}
          {actionError && <Message type="error" content={actionError} />}
        </>
      )}
      {showContent && view === MFA_VIEW.SETUP && setupData && (
        <MfaSetupModal setupData={setupData} onCancel={cancel} onSubmit={confirmEnable} />
      )}
      {showContent && view === MFA_VIEW.BACKUP_CODES && (
        // Non-dismissible: MFA is already enabled by this point, so the only way out is the
        // gated "Done" inside BackupCodes (requires the "I've saved my recovery codes"
        // checkbox). Wiring cancel/dismiss to finishBackupCodes would clear the one-time codes
        // without that confirmation, so a user who never copied them would lose access.
        <Modal title="Save your backup codes" onCancel={() => {}}>
          <BackupCodes codes={backupCodes} onDone={finishBackupCodes} fullWidth />
        </Modal>
      )}
      {showContent && view === MFA_VIEW.DISABLE && (
        <MfaCodeModal variant="disable" onSubmit={confirmDisable} onCancel={cancel} dataCyPrefix="mfa-disable" />
      )}
      {showContent && view === MFA_VIEW.REGENERATE && (
        <MfaCodeModal
          variant="regenerate"
          onSubmit={confirmRegenerate}
          onCancel={cancel}
          dataCyPrefix="mfa-regenerate"
        />
      )}
    </Flex>
  );
};

export default MFASettings;
